import { Capacitor } from '@capacitor/core';
import { useAuth } from '@mdvip/shared/contexts/AuthContext';
import { useProactiveTokenRefresh } from '@mdvip/shared/hooks/useProactiveTokenRefresh';
import { lazy, Suspense } from 'react';
import { IdleTimeoutWarningDialog } from './IdleTimeoutWarningDialog';
import { useIdleTimeout } from './useIdleTimeout';

// Dynamic import keeps MobileIdleTimeoutManager and @capacitor/app out of the
// web bundle. React.lazy only triggers the import when React renders the component;
// on web isNativePlatform() is false so MobileIdleTimeoutManager is never rendered
// and this chunk is never fetched.
// React.lazy requires a default export. MobileIdleTimeoutManager uses a named export,
// so we re-shape the module inline rather than changing its public API.
const MobileIdleTimeoutManager = lazy(() =>
  import('./MobileIdleTimeoutManager').then((m) => ({ default: m.MobileIdleTimeoutManager })),
);

// HIPAA-compliant defaults — can be overridden via config.json per environment
const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_WARNING_DURATION_MS = 20 * 1000; // 20 seconds

interface IdleTimeoutManagerProps {
  idleTimeoutMs?: number;
  warningDurationMs?: number;
  tokenRefreshThreshold?: number;
}

/**
 * Side-effect component that enforces idle session timeout.
 *
 * Render this as a sibling of <BrowserRouter> inside App.tsx — it is already
 * within ConfigProvider and AuthProvider, so useAuth() works without any
 * additional wrapping. It renders only the warning dialog; all timer logic
 * lives in useIdleTimeout.
 *
 * On native platforms (iOS/Android), delegates to MobileIdleTimeoutManager
 * which uses wall-clock idle detection via appStateChange. App-level code
 * never needs to know which implementation is active.
 */
export function IdleTimeoutManager({
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  warningDurationMs = DEFAULT_WARNING_DURATION_MS,
  tokenRefreshThreshold,
}: IdleTimeoutManagerProps) {
  if (Capacitor.isNativePlatform()) {
    // Suspense fallback=null: MobileIdleTimeoutManager is a background side-effect
    // component with no visible UI of its own (it renders only the warning dialog).
    // The ~30min idle window makes the brief async load time negligible.
    return (
      <Suspense fallback={null}>
        <MobileIdleTimeoutManager
          idleTimeoutMs={idleTimeoutMs}
          warningDurationMs={warningDurationMs}
          tokenRefreshThreshold={tokenRefreshThreshold}
        />
      </Suspense>
    );
  }

  // Web implementation below — hooks must be called unconditionally so this
  // early return only wraps the JSX, not the hooks themselves.
  return (
    <IdleTimeoutManagerWeb
      idleTimeoutMs={idleTimeoutMs}
      warningDurationMs={warningDurationMs}
      tokenRefreshThreshold={tokenRefreshThreshold}
    />
  );
}

function IdleTimeoutManagerWeb({
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  warningDurationMs = DEFAULT_WARNING_DURATION_MS,
  tokenRefreshThreshold,
}: IdleTimeoutManagerProps) {
  const { user, logout, refresh } = useAuth();

  useProactiveTokenRefresh({ threshold: tokenRefreshThreshold, isEnabled: user !== null });

  const { showWarning, remainingSeconds, extendSession, triggerLogout } = useIdleTimeout({
    idleTimeoutMs,
    warningDurationMs,
    onLogout: logout,
    onRefresh: refresh,
    isEnabled: user !== null,
  });

  return (
    <IdleTimeoutWarningDialog
      open={showWarning}
      remainingSeconds={remainingSeconds}
      onContinue={extendSession}
      onLogout={triggerLogout}
    />
  );
}
